import React, { useEffect, useMemo, useRef, useState } from "react";
// ⚠️ THE CONNECTORS LOADER'S STYLESHEET, ON PURPOSE. Every `wcl-*` class below
// comes from it, and none of those rules are nested under that screen's id — so
// they are reusable by name (checked before relying on it; the ancestor-scoped
// trap is real elsewhere in this codebase).
//
// Reused rather than copied because these two waits are seen MINUTES APART in
// one live session: the connector map is drafted, both parties review it,
// Confirm Connections fires, and the shortlist agent starts. Two loading
// screens that were nearly the same would read as two different products. This
// way they cannot drift — a change to the progress bar or the step cards lands
// on both.
import "../ExecutionBlueprintWigConnections/WigConnectionsLoading.scss";
import "./YearOnePlan.scss";

// ═════════════════════════════════════════════════════════════════════════════
// "We're building your Year 1 Plan" — the `year1_drafting` wait.
//
// ⚠️ THIS RESTORES SOMETHING THAT WAS DELIBERATELY REMOVED, so the history
// matters. §6b.2 asked for a Year 1 wait "of the same shape as the connectors
// loading screen". It was built that way, then reduced to a bare full-screen
// spinner on instruction (2026-08-31) and its route deleted. It is now a
// designed wait again, on instruction (2026-09-03) — and `DRAFT_COPY` in
// `WigConnectionsLoading.js` was left keyed by wait rather than flattened
// precisely so this could come back as content rather than as a refactor.
//
// It is a COMPONENT, not a route, and that is the one structural difference
// from the connectors wait. The Year 1 Plan screen owns both `year1_drafting`
// and `year1_review` (see its `OWN_STEPS`), so the wait renders in place and
// the step flip swaps it for the plan with no navigation. The connectors wait
// is a route because its two steps live on two different screens.
//
// Consequently this file holds NO socket, NO routing and NO breadcrumbs: the
// screen that mounts it has already joined, is already listening for
// `live_review_step_changed`, and already owns the trail. That is why there is
// no re-join safety net here and there must not be one — two components racing
// to `join()` on one namespace is worse than either.
// ═════════════════════════════════════════════════════════════════════════════

const COPY = {
  title: "We’re preparing the 12-month priorities",
  subtitle:
    "The AI is using the connection map to find the WIGs with the most leverage, and proposing an owner for each.",
  steps: [
    {
      key: "reading",
      glyph: "◎",
      title: "Reading the connections",
      text: "Counting what each WIG contributes to and depends on.",
    },
    {
      key: "weighing",
      glyph: "⚖",
      title: "Weighing leverage",
      text: "Finding the WIGs the rest of the blueprint rests on.",
    },
    {
      key: "shortlisting",
      glyph: "✓",
      title: "Prioritising for the next 12 months",
      text: "Picking the WIGs that matter most this year.",
    },
    {
      key: "owners",
      glyph: "☺",
      title: "Proposing owners",
      text: "Suggesting someone from the leadership team for each one.",
    },
  ],
  banner: {
    title: "Why prioritise",
    // ⚠️ NO NUMBER IN THIS COPY. §6b.4 is explicit that the agent has no target
    // count — it guarantees at least one per section and otherwise picks on
    // leverage. Saying "we'll pick six" would be inventing a promise the
    // backend never made, and the CEO would count them.
    text: "Fewer WIGs, sharper focus. These are a starting point, and you'll adjust them together with the CEO on the next screen.",
  },
};

// ── The progress model ──────────────────────────────────────────────────────
// Copied from the connectors wait, values and reasoning intact, because it is
// the same problem: an agent run of unknown length with nothing to report.
//
//   - The bar eases toward a CEILING it never reaches. It always moves and
//     never completes on its own; a bar sitting at 100% while work continues
//     is the fastest way for a loading screen to lose trust.
//   - Each tick advances a fraction of the REMAINING distance, so it
//     decelerates and a long wait never runs out of runway.
//   - The four steps land a check on a timer. Discrete milestones make a wait
//     feel shorter than one bar does, because something visibly finishes.
//   - After 45s the reassurance changes, rather than repeating "less than a
//     minute" at someone who has already waited longer than that.
const CEILING = 92;
const TICK_MS = 400;
const SLOW_AFTER_MS = 45000;
const DEFAULT_DURATION_MS = 40000;

// ── The mark ────────────────────────────────────────────────────────────────
// The connectors wait draws a graph assembling itself. This draws the thing
// THIS agent is making: a shortlist. Four rows arrive in sequence, a tick
// strokes itself into each one, and an owner dot lands beside it — so the
// picture is "goals being chosen and assigned", which is exactly what the
// four step cards below say in words.
//
// Deliberately built from the same parts as `ConnectionMark`: teal fills, a
// `#8fd3d6` accent, a sparkle, and animation driven by `stroke-dashoffset` and
// opacity rather than by transforms on the whole SVG. Same family, different
// subject.
const ShortlistMark = () => (
  <svg
    className="wcl-mark yop-mark"
    viewBox="0 0 202 138"
    width="202"
    height="138"
    role="img"
    aria-label="Preparing the 12-month priorities"
  >
    {/* The four rows. `--i` staggers every animation on the row, so one custom
        property drives the card, its tick and its owner dot together. */}
    {[0, 1, 2, 3].map((row) => (
      <g className="yop-mark-row" style={{ "--i": row }} key={row}>
        <rect
          className="yop-mark-card"
          x="30"
          y={18 + row * 29}
          width="118"
          height="21"
          rx="6"
        />
        <path
          className="yop-mark-tick"
          d={`M38 ${29 + row * 29} l5 5 9-10`}
          fill="none"
          stroke="#0d7a82"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle className="yop-mark-owner" cx="163" cy={28 + row * 29} r="8" />
      </g>
    ))}
    <g fill="#8fd3d6" fontSize="13" fontWeight="700">
      <text x="176" y="16">
        ✦
      </text>
      <text className="wcl-mark-spark" x="14" y="120" fontSize="9">
        ✦
      </text>
    </g>
  </svg>
);

// ⚠️ Inlined rather than imported, and this is the same call `PersonIcon` makes
// in `YearOnePlan.js`: the two apps keep their icon modules at DIFFERENT paths
// (`StrategyCanvas/StrategyCanvasHelpers` in the coach app,
// `ExecutionBlueprintLiveReview/_design/icons` in the CEO app), so importing a
// shared `CheckIcon` is the one thing that would stop this file being
// byte-identical across the two repos. Eleven lines is a cheap price for that.
const StepTick = () => (
  <svg
    width="10"
    height="10"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M3 8.5 6.2 11.7 13 4.9"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StepCard = ({ step, state }) => (
  <div className={`wcl-step wcl-step--${state}`}>
    <div className="wcl-step-icon">
      <span className="wcl-step-glyph" aria-hidden="true">
        {step.glyph}
      </span>
      {state === "done" && (
        <span className="wcl-step-badge">
          <StepTick />
        </span>
      )}
      {/* Exactly one ring at a time — the step actually in flight. */}
      {state === "active" && <span className="wcl-step-ring" />}
    </div>
    <p className="wcl-step-title">{step.title}</p>
    <p className="wcl-step-text">{step.text}</p>
  </div>
);

/**
 * @param done       True once the work is really finished. In practice the step
 *                   flip unmounts this instead, so the bar is normally
 *                   abandoned mid-ease — which is correct: the wait ends because
 *                   the plan arrived, not because a bar filled up.
 * @param durationMs Paces the step ticks only.
 * @param joinError  The mounting screen's socket error, if any. Passed in
 *                   rather than read here, because this component owns no
 *                   socket — see the header.
 */
const YearOnePlanLoading = ({
  done = false,
  durationMs = DEFAULT_DURATION_MS,
  joinError = null,
}) => {
  const [percent, setPercent] = useState(6);
  const [stepIndex, setStepIndex] = useState(0);
  const [slow, setSlow] = useState(false);
  const startedAt = useRef(Date.now());
  const steps = COPY.steps;

  useEffect(() => {
    if (done) return undefined;
    const timer = setInterval(() => {
      setPercent((prev) => prev + (CEILING - prev) * 0.06);
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [done]);

  // Stops ON the last step rather than marking it done — the final step only
  // completes for real, when the plan arrives.
  useEffect(() => {
    if (done) return undefined;
    const per = Math.max(durationMs / steps.length, 1200);
    const timer = setInterval(() => {
      setStepIndex((prev) => Math.min(prev + 1, steps.length - 1));
    }, per);
    return () => clearInterval(timer);
  }, [done, durationMs, steps.length]);

  useEffect(() => {
    if (done) return undefined;
    const timer = setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, [done]);

  useEffect(() => {
    if (!done) return;
    setPercent(100);
    setStepIndex(steps.length);
  }, [done, steps.length]);

  const stepStates = useMemo(
    () =>
      steps.map((_, index) => {
        if (index < stepIndex) return "done";
        if (index === stepIndex) return "active";
        return "pending";
      }),
    [stepIndex, steps],
  );

  const elapsed = Math.round((Date.now() - startedAt.current) / 1000);

  return (
    <div className="yop-drafting">
      <div className="wcl-inner">
        <ShortlistMark />

        <h1 className="wcl-title">{COPY.title}</h1>
        <p className="wcl-subtitle">{COPY.subtitle}</p>

        {/* `aria-valuenow` is omitted while indeterminate: announcing a number
            this screen invented would be worse than announcing none. */}
        <div
          className="wcl-progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={done ? 100 : undefined}
          aria-label="Preparing the 12-month priorities"
        >
          <div className="wcl-progress-track">
            <div
              className={`wcl-progress-fill ${done ? "is-done" : ""}`}
              style={{ width: `${Math.min(percent, 100)}%` }}
            />
          </div>
        </div>

        <div className="wcl-meta" aria-live="polite">
          {/* A failed join outranks both other states. Without this branch a
              socket that never joined looks identical to an agent still
              working — the bar eases and the steps tick either way, because
              both are on timers — so the one state the user can act on would
              be the one they cannot see. */}
          {joinError ? (
            <>
              <span className="wcl-meta-line">
                We can&apos;t reach the live session right now.
              </span>
              <span className="wcl-meta-sub">
                Your blueprint is safe and nothing has been lost. This page
                keeps retrying on its own — if it doesn&apos;t recover, reload.
              </span>
            </>
          ) : slow && !done ? (
            <>
              <span className="wcl-meta-line">
                Still working — this one&apos;s taking a little longer than
                usual.
              </span>
              <span className="wcl-meta-sub">
                Nothing&apos;s wrong, and nothing will be lost. It will appear here as soon as it&apos;s ready
                {elapsed ? ` (${elapsed}s so far)` : ""}.
              </span>
            </>
          ) : (
            <>
              <span className="wcl-meta-line">
                <span className="wcl-meta-clock" aria-hidden="true">
                  🕐
                </span>
                This usually takes less than a minute.
              </span>
              <span className="wcl-meta-sub">
                Your work is saved as you go.
              </span>
            </>
          )}
        </div>

        {/* The connector between cards fills as each step completes, so the
            row reads as one run rather than four separate tiles. Same markup
            as the connectors wait — its `.wcl-step-connector` rules expect a
            sibling between every pair. */}
        <div className="wcl-steps">
          {steps.map((step, index) => (
            <React.Fragment key={step.key}>
              {index > 0 && (
                <div className="wcl-step-connector" aria-hidden="true">
                  <span
                    className={`wcl-step-connector-line ${
                      stepStates[index] !== "pending" ? "is-filled" : ""
                    }`}
                  />
                </div>
              )}
              <StepCard step={step} state={stepStates[index]} />
            </React.Fragment>
          ))}
        </div>

        <div className="wcl-banner">
          <span className="wcl-banner-spark" aria-hidden="true">
            ✦
          </span>
          <div>
            <p className="wcl-banner-title">{COPY.banner.title}</p>
            <p className="wcl-banner-text">{COPY.banner.text}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YearOnePlanLoading;
